/**
 * AssemblyAIWebSocketService.js
 * Handles real-time WebSocket connection to AssemblyAI for live transcription.
 *
 * Live transcription is a preview (the saved transcript is re-done in batch),
 * but AssemblyAI bills for every second a session is open, so the lifecycle
 * keeps four rules:
 *  1. At most one socket at a time. Nothing opens while another socket is
 *     connecting, open or terminating.
 *  2. Every socket that opened is sent `Terminate` before the client closes it.
 *  3. Nothing opens unless `isWanted()` says so (checked before every open and
 *     again when the socket opens), and `epoch` invalidates every async
 *     continuation (timer, token fetch, connect) started before a
 *     terminate / disconnect / reconnect.
 *  4. One owner schedules retries. A dropped established session schedules a
 *     retry from `onclose`; a failed connect attempt rejects `connect()` and its
 *     caller schedules the retry.
 */
import { liveStreamingUrl } from "@/constants/liveTranscription";

// Sockets detached while still connecting, shared by EVERY service instance
// (a recording's service can be replaced while its handshake is in flight):
// socket → { promise, done }. Each counts as an open socket for rule 1 until
// it opened-and-was-terminated, closed, or timed out, so the invariant holds
// per page, not just per instance.
const detachedSockets = new Map();

/** Test isolation only: forget detached sockets from earlier tests. */
export function _resetDetachedSocketsForTests() {
  for (const cleanup of detachedSockets.values()) cleanup.done();
  detachedSockets.clear();
}

export const TOKEN_TIMEOUT_MS = 10000;
export const OPEN_TIMEOUT_MS = 10000;
export const TERMINATE_TIMEOUT_MS = 3000;

function withDeadline(promise, ms, label) {
  let timer;
  return Promise.race([
    Promise.resolve(promise).finally(() => clearTimeout(timer)),
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error(`${label} timed out`)), ms);
    }),
  ]);
}

export class AssemblyAIWebSocketService {
  /**
   * @param {string} token - Initial realtime token (from /initialize), valid
   *   for the FIRST connect.
   * @param {Object} [options]
   * @param {() => Promise<string|null>} [options.tokenProvider] - Returns a
   *   FRESH realtime token. Every reconnect uses it: the initial token is
   *   short-lived.
   * @param {string|null} [options.language] - The recording's transcription
   *   language ("auto" or unset = auto-detect). Picks the speech model.
   * @param {() => boolean} [options.isWanted] - Whether a live session should
   *   exist right now (the recording is actually recording). Gates every open.
   * @param {() => number} [options.nextSessionIndex] - Allocates the index of
   *   each socket as it is created (analytics; failed attempts count too, as
   *   on Android). Passed back with that socket's Begin and Termination.
   */
  constructor(token, options = {}) {
    this.token = token;
    this.tokenProvider = options.tokenProvider || null;
    this.language = options.language || null;
    this.isWanted = options.isWanted || (() => true);
    this.nextSessionIndex = options.nextSessionIndex || (() => null);
    this.socket = null;
    this.sessionId = null;
    this.connected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.epoch = 0;
    this.reconnectTimer = null;
    this.retryFlight = null;
    // { socket, reject } for a connect() still waiting for its socket to open.
    this.pendingConnect = null;
    // { socket, promise, finish } while terminate() winds a session down.
    this.terminating = null;
    this.listeners = {
      onTranscript: null,
      onError: null,
      onConnect: null,
      onDisconnect: null,
      onSessionBegins: null,
      onSessionTerminated: null,
      onReconnecting: null,
      onReconnected: null,
      onReconnectFailed: null,
    };
  }

  /** @private */
  _canOpen(epoch) {
    return epoch === this.epoch && this.isWanted();
  }

  /**
   * Fetch a fresh token via the provider (if configured), bounded by
   * TOKEN_TIMEOUT_MS. A token that arrives after `epoch` moved on is ignored.
   * Best-effort: on failure the existing token is kept.
   * @private
   */
  async _refreshToken(epoch = this.epoch) {
    if (!this.tokenProvider) return;
    try {
      const fresh = await withDeadline(
        this.tokenProvider(),
        TOKEN_TIMEOUT_MS,
        "Realtime token",
      );
      if (fresh && epoch === this.epoch) {
        this.token = fresh;
        console.log("[AssemblyAI] Refreshed realtime token for reconnect");
      }
    } catch (error) {
      console.error("[AssemblyAI] Failed to refresh realtime token:", error);
    }
  }

  /** @private */
  _cancelPendingReconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    // An in-flight retry no longer owns the retry slot; its completion will
    // not schedule a successor.
    this.retryFlight = null;
  }

  /**
   * Ask AssemblyAI to end the session (billing stops on Terminate).
   * Best-effort: only possible while the socket is open.
   * @private
   */
  _sendTerminate(socket) {
    if (socket?.readyState !== WebSocket.OPEN) return;
    try {
      socket.send(JSON.stringify({ type: "Terminate" }));
    } catch (error) {
      console.warn("[AssemblyAI] Failed to send Terminate:", error);
    }
  }

  /**
   * Terminate and close the current socket now. Settles a connect() still
   * waiting on it and a terminate() winding it down.
   * @private
   */
  _closeSocket(reason) {
    const socket = this.socket;
    this.socket = null;
    this.sessionId = null;
    this.connected = false;
    if (!socket) return;
    if (socket.readyState === WebSocket.CONNECTING) {
      // Aborting the handshake could leave a session the server already
      // accepted without a Terminate. Detach it instead: if it opens, its
      // onopen sees it is no longer current and sends Terminate before
      // closing; if it never opens, its open timer closes it. Until then no
      // other socket may open (see _whenIdle).
      if (!detachedSockets.has(socket)) {
        const cleanup = { promise: null, done: null };
        cleanup.promise = new Promise((resolve) => {
          cleanup.done = resolve;
        });
        detachedSockets.set(socket, cleanup);
      }
    } else {
      this._sendTerminate(socket);
      try {
        socket.close(1000, reason);
      } catch (error) {
        console.warn("[AssemblyAI] Failed to close socket:", error);
      }
    }
    if (this.pendingConnect?.socket === socket) {
      this.pendingConnect.reject(new Error(reason));
    }
    if (this.terminating?.socket === socket) {
      this.terminating.finish();
    }
  }

  /** @private */
  _detachedDone(socket) {
    const cleanup = detachedSockets.get(socket);
    if (cleanup) {
      detachedSockets.delete(socket);
      cleanup.done();
    }
  }

  /** @private */
  _busy() {
    return Boolean(this.terminating) || detachedSockets.size > 0;
  }

  /**
   * Resolves once no earlier socket is still terminating or connecting
   * (bounded by TERMINATE_TIMEOUT_MS / OPEN_TIMEOUT_MS), so a new socket never
   * overlaps one that may still be billed.
   * @private
   */
  async _whenIdle() {
    if (!this._busy()) return;
    await Promise.all([
      this.terminating?.promise,
      ...[...detachedSockets.values()].map((c) => c.promise),
    ]);
    // Something new may have started winding down meanwhile.
    await this._whenIdle();
  }

  /**
   * Start the live session (first connect). Never rejects: a failure goes to
   * the retry owner.
   * @returns {Promise<void>}
   */
  async start() {
    this._cancelPendingReconnect();
    const epoch = ++this.epoch;
    this.reconnectAttempts = 0;
    try {
      await this.connect(epoch);
    } catch (error) {
      console.error("[AssemblyAI] Initial connect failed:", error);
      if (this._canOpen(epoch)) this.attemptReconnect();
    }
  }

  /**
   * Re-establish the stream (resume, page restored): waits for any session
   * still terminating (so its last turn is kept and two sessions never
   * overlap), then connects with a fresh token. Never rejects.
   * @returns {Promise<void>}
   */
  async reconnect() {
    this._cancelPendingReconnect();
    const epoch = ++this.epoch;
    this.reconnectAttempts = 0;
    // An open socket that isn't winding down is replaced; one still
    // connecting is detached. Then wait for every earlier socket to finish.
    if (this.socket && this.socket !== this.terminating?.socket) {
      this._closeSocket("Reconnecting");
    }
    if (this._busy()) await this._whenIdle();
    if (!this._canOpen(epoch)) return;
    if (this.listeners.onReconnecting) {
      this.listeners.onReconnecting(0);
    }
    await this._refreshToken(epoch);
    if (!this._canOpen(epoch)) return;
    try {
      await this.connect(epoch);
      if (this.listeners.onReconnected) {
        this.listeners.onReconnected();
      }
    } catch (error) {
      console.error("[AssemblyAI] Explicit reconnect failed:", error);
      if (this._canOpen(epoch)) this.attemptReconnect();
    }
  }

  /**
   * Open one socket. Always settles: resolves on open; rejects if the socket
   * closes or errors before opening, does not open within OPEN_TIMEOUT_MS, is
   * superseded, or is no longer wanted when it opens (it is then sent
   * Terminate and closed).
   * @param {number} [epoch]
   * @returns {Promise<void>}
   */
  connect(epoch = this.epoch) {
    if (!this._canOpen(epoch)) {
      return Promise.reject(new Error("Live transcription not wanted"));
    }
    // Rule 1, enforced here for every caller: never two sockets. An existing
    // socket that isn't winding down is replaced (detached if still
    // connecting); then any earlier socket must finish before this one opens.
    if (this.socket && this.socket !== this.terminating?.socket) {
      this._closeSocket("Superseded");
    }
    if (this._busy()) {
      return this._whenIdle().then(() => this.connect(epoch));
    }
    return this._open(epoch);
  }

  /** @private */
  _open(epoch) {
    return new Promise((resolve, reject) => {
      let settled = false;
      let opened = false;
      let socket;
      let sessionMeta;
      let openTimer = null;
      const settle = (error) => {
        if (settled) return;
        settled = true;
        if (this.pendingConnect?.socket === socket) this.pendingConnect = null;
        if (error) reject(error);
        else resolve();
      };

      try {
        // v3 streaming; the speech model follows the recording's language.
        socket = new WebSocket(liveStreamingUrl(this.token, this.language));
        // This socket's own session metadata, so its Begin and Termination are
        // never attributed to another socket.
        sessionMeta = {
          sessionIndex: this.nextSessionIndex(),
          sessionId: null,
        };
      } catch (error) {
        console.error("Failed to create WebSocket:", error);
        settle(error);
        return;
      }
      this.socket = socket;
      this.pendingConnect = { socket, reject: settle };
      const isCurrent = () => socket === this.socket;

      // Runs even after the attempt settled (e.g. the socket was detached
      // while connecting), so a handshake that hangs is always closed.
      openTimer = setTimeout(() => {
        if (opened) return;
        if (isCurrent()) {
          this.socket = null;
          this.connected = false;
        }
        try {
          socket.close(1000, "Open timed out");
        } catch {
          // Already closing.
        }
        this._detachedDone(socket);
        settle(new Error("Live transcription socket did not open in time"));
      }, OPEN_TIMEOUT_MS);

      socket.onopen = () => {
        opened = true;
        clearTimeout(openTimer);
        if (!isCurrent() || !this._canOpen(epoch)) {
          // Superseded or no longer wanted: end it before it bills.
          this._sendTerminate(socket);
          try {
            socket.close(1000, "Superseded");
          } catch {
            // Already closing.
          }
          if (isCurrent()) {
            this.socket = null;
            this.connected = false;
          }
          this._detachedDone(socket);
          settle(new Error("Superseded"));
          return;
        }
        console.log("Connected to AssemblyAI WebSocket");
        this.connected = true;
        this.reconnectAttempts = 0;
        // From here a drop is retried by onclose, so a retry flight that
        // opened this socket no longer holds the retry slot.
        this.retryFlight = null;
        if (this.listeners.onConnect) {
          this.listeners.onConnect();
        }
        settle();
      };

      socket.onmessage = (event) => {
        if (!isCurrent()) return;
        this.handleMessage(event.data, sessionMeta);
      };

      socket.onerror = (error) => {
        if (!isCurrent()) return;
        console.error("AssemblyAI WebSocket error:", error);
        if (!opened) {
          // The caller of connect() owns the retry for a failed attempt.
          this.socket = null;
          this.connected = false;
          settle(error);
          return;
        }
        if (this.listeners.onError) {
          this.listeners.onError(error);
        }
      };

      socket.onclose = (event) => {
        clearTimeout(openTimer);
        if (!opened) {
          this._detachedDone(socket);
          if (isCurrent()) {
            this.socket = null;
            this.connected = false;
          }
          settle(new Error(`Socket closed before opening (${event.code})`));
          return;
        }
        if (!isCurrent()) return;
        console.log("AssemblyAI WebSocket closed:", event.code, event.reason);
        this.socket = null;
        this.sessionId = null;
        this.connected = false;
        if (this.listeners.onDisconnect) {
          this.listeners.onDisconnect(event);
        }
        if (this.terminating?.socket === socket) {
          // A session we are winding down ended; never reconnect it.
          this.terminating.finish();
          return;
        }
        if (event.code !== 1000) {
          this.attemptReconnect();
        }
      };
    });
  }

  /**
   * End the session gracefully (stop / pause): send Terminate, keep accepting
   * its last turns until AssemblyAI confirms with `Termination` (or closes, or
   * `timeoutMs` passes), then close. While it winds down no audio is sent and
   * nothing else can open. Never rejects; idempotent per socket.
   * @param {Object} [options]
   * @param {number} [options.timeoutMs]
   * @returns {Promise<void>}
   */
  terminate({ timeoutMs = TERMINATE_TIMEOUT_MS } = {}) {
    this.epoch++;
    this._cancelPendingReconnect();
    const socket = this.socket;
    if (this.terminating?.socket === socket && socket) {
      return this.terminating.promise;
    }
    if (socket?.readyState !== WebSocket.OPEN) {
      this._closeSocket("User initiated disconnect");
      return Promise.resolve();
    }

    const waiter = { socket, promise: null, finish: null };
    waiter.promise = new Promise((resolve) => {
      let timer = null;
      waiter.finish = () => {
        if (!timer) return; // already finished
        clearTimeout(timer);
        timer = null;
        if (this.terminating === waiter) this.terminating = null;
        if (this.socket === socket) {
          this.socket = null;
          this.sessionId = null;
          this.connected = false;
        }
        try {
          socket.close(1000, "Session terminated");
        } catch {
          // Already closed.
        }
        resolve();
      };
      timer = setTimeout(() => waiter.finish(), timeoutMs);
    });
    this.terminating = waiter;
    this._sendTerminate(socket);
    return waiter.promise;
  }

  /**
   * Handle incoming messages from AssemblyAI
   * @param {string} data - Raw message data
   */
  handleMessage(data, sessionMeta = { sessionIndex: null, sessionId: null }) {
    try {
      const message = JSON.parse(data);

      // Handle both v2 and v3 message formats
      const messageType = message.message_type || message.type;

      // Check if message has a valid type
      if (!messageType) {
        console.log("Received message without type:", message);
        return;
      }

      switch (messageType) {
        case "SessionBegins":
        case "Begin":
          // v3 uses 'Begin', v2 uses 'SessionBegins'
          this.sessionId = message.session_id || message.id;
          console.log("AssemblyAI session started:", this.sessionId);
          if (this.listeners.onSessionBegins) {
            sessionMeta.sessionId = message.id || message.session_id || null;
            this.listeners.onSessionBegins(message, sessionMeta);
          }
          break;

        case "Turn":
          // v3: a turn is re-sent (same turn_order) as it grows. It is final
          // only at end of turn, and only once formatted — with format_turns
          // an unformatted end-of-turn copy can precede the formatted one.
          if (this.listeners.onTranscript && message.transcript) {
            this.listeners.onTranscript({
              text: message.transcript,
              isFinal:
                message.end_of_turn === true &&
                message.turn_is_formatted !== false,
              timestamp: Date.now(),
              turnOrder: message.turn_order,
            });
          }
          break;

        case "SpeechStarted":
          // Pro-only voice-activity event; nothing to show.
          break;

        case "PartialTranscript":
          // v2 format - interim/partial transcription result
          if (this.listeners.onTranscript) {
            this.listeners.onTranscript({
              text: message.text,
              isFinal: false,
              timestamp: Date.now(),
              audioStart: message.audio_start,
              audioEnd: message.audio_end,
            });
          }
          break;

        case "FinalTranscript":
          // v2 format - final transcription result
          if (this.listeners.onTranscript) {
            this.listeners.onTranscript({
              text: message.text,
              isFinal: true,
              timestamp: Date.now(),
              audioStart: message.audio_start,
              audioEnd: message.audio_end,
            });
          }
          break;

        case "SessionTerminated":
        case "Termination":
        case "Terminate":
          console.log("AssemblyAI session terminated");
          if (this.terminating?.socket === this.socket) {
            this.terminating.finish();
          }
          if (this.listeners.onSessionTerminated) {
            this.listeners.onSessionTerminated(message, sessionMeta);
          }
          break;

        case "RealtimeError":
        case "Error": {
          const errorMessage = message.error || message.message;
          console.error("AssemblyAI error:", errorMessage);
          if (this.listeners.onError) {
            this.listeners.onError(new Error(errorMessage));
          }
          break;
        }

        default:
          console.log("Unknown message type:", messageType, message);
      }
    } catch (error) {
      console.error("Failed to parse AssemblyAI message:", error);
      if (this.listeners.onError) {
        this.listeners.onError(error);
      }
    }
  }

  /**
   * Send audio data to AssemblyAI
   * @param {ArrayBuffer} audioData - PCM audio data (16-bit, 16kHz, mono)
   */
  sendAudio(audioData) {
    if (
      !this.isConnected ||
      !this.socket ||
      this.socket.readyState !== WebSocket.OPEN
    ) {
      console.warn("Cannot send audio: WebSocket not connected");
      return;
    }

    try {
      // v3 API expects raw binary data, not JSON-wrapped base64
      // Convert ArrayBuffer to Uint8Array if needed
      const audioBytes =
        audioData instanceof ArrayBuffer
          ? new Uint8Array(audioData)
          : audioData;

      console.log(
        `[AssemblyAI] Sending audio data: ${audioBytes.byteLength} bytes (binary)`,
      );
      this.socket.send(audioBytes);
    } catch (error) {
      console.error("Failed to send audio:", error);
      if (this.listeners.onError) {
        this.listeners.onError(error);
      }
    }
  }

  /**
   * Convert ArrayBuffer to base64 string
   * @param {ArrayBuffer} buffer
   * @returns {string}
   */
  arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * Schedule the next retry with exponential backoff. The single retry owner:
   * a no-op while a retry is pending or running, or when no session is wanted.
   * After `maxReconnectAttempts` it gives up (onReconnectFailed + onError).
   */
  attemptReconnect() {
    if (this.reconnectTimer || this.retryFlight) return;
    if (!this.isWanted()) return;
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error("Max reconnection attempts reached");
      if (this.listeners.onReconnectFailed) {
        this.listeners.onReconnectFailed();
      }
      if (this.listeners.onError) {
        this.listeners.onError(
          new Error("Failed to reconnect after maximum attempts"),
        );
      }
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1); // Exponential backoff

    console.log(
      `Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`,
    );

    if (this.listeners.onReconnecting) {
      this.listeners.onReconnecting(this.reconnectAttempts);
    }

    const epoch = this.epoch;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      const flight = {};
      this.retryFlight = flight;
      // _runRetry handles its own failures; this only guards the unexpected.
      this._runRetry(epoch, flight).catch((error) => {
        console.error("[AssemblyAI] Retry failed unexpectedly:", error);
      });
    }, delay);
  }

  /** @private */
  async _runRetry(epoch, flight) {
    // Releases the retry slot only if this flight still owns it, so a
    // cancelled flight can't free (or reschedule over) a newer one.
    const release = () => {
      if (this.retryFlight !== flight) return false;
      this.retryFlight = null;
      return true;
    };
    // Fetch a fresh token first — the original is short-lived and will be
    // expired by now, so reusing it would make every reconnect fail.
    await this._refreshToken(epoch);
    if (!this._canOpen(epoch)) {
      release();
      return;
    }
    try {
      await this.connect(epoch);
      release();
      console.log("Reconnection successful");
      if (this.listeners.onReconnected) {
        this.listeners.onReconnected();
      }
    } catch (error) {
      console.error("Reconnection failed:", error);
      if (release() && this._canOpen(epoch)) {
        this.attemptReconnect();
      }
    }
  }

  /**
   * Disconnect from AssemblyAI immediately (reset, page going away): sends
   * Terminate, closes without waiting, and cancels any reconnect in flight.
   * Prefer terminate() when there is time to wait for the last turn.
   */
  disconnect() {
    this.epoch++;
    this._cancelPendingReconnect();
    this._closeSocket("User initiated disconnect");
  }

  /**
   * Set event listeners
   * @param {Object} listeners
   */
  setListeners(listeners) {
    this.listeners = { ...this.listeners, ...listeners };
  }

  /**
   * Get connection status
   * @returns {boolean}
   */
  get isConnected() {
    return Boolean(
      this.socket &&
        this.socket !== this.terminating?.socket &&
        this.socket.readyState === WebSocket.OPEN,
    );
  }

  /**
   * Configure punctuation and formatting
   * NOTE: AssemblyAI configuration is done via URL parameters, not through WebSocket messages
   */
  configure(options = {}) {
    // Configuration is handled via URL parameters during connection
    console.log("Configuration options noted for next connection:", options);
  }
}

export default AssemblyAIWebSocketService;
